import React, { useEffect, useState } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import SlideBase from './SlideBase'
import Caption from 'LuxKit/Typography/Caption'
import Group from 'components/utils/Group'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import FadeBackground from '../FadeBackground'
import { useWrappedStorySelector } from 'contexts/Wrapped'
import { WRAPPED_SLIDES } from '../../config/slides'
import FormatDecimal from 'components/utils/Formatters/FormatDecimal'
import useVideoBackgroundWithFallback from '../../hooks/useVideoBackgroundWithFallback'

// Get slide config for this slide (static, doesn't depend on runtime data)
const SLIDE_CONFIG = WRAPPED_SLIDES.find(s => s.id === 'offer-views')
const SLIDE_ID = 'offer-views' as const

function OfferViewsSlide() {
  const intl = useIntl()
  // Use granular selectors to minimize re-renders
  const data = useWrappedStorySelector(state => state.data)
  const totalsData = useWrappedStorySelector(state => state.totalsData)

  // Use the video background with fallback hook
  const { background, isBackgroundReady, isCurrentSlide } = useVideoBackgroundWithFallback({
    slideConfig: SLIDE_CONFIG,
    slideId: SLIDE_ID,
  })

  const [animate, setAnimate] = useState(!SLIDE_CONFIG?.video) // If no video, animate immediately

  // Totals-only mode is determined by not having user wrapped data
  const isTotalsOnlyMode = !data

  // Start animation when background is ready (video loaded or fallback image)
  useEffect(() => {
    if (SLIDE_CONFIG?.video && isBackgroundReady && isCurrentSlide) {
      setAnimate(true)
    }
  }, [isBackgroundReady, isCurrentSlide])

  // Need either user data or totals data to render
  if (!data && !totalsData) return null

  const totalOffersViewed = totalsData?.stats.offersViewed ?? 0
  const averageOffersViewed = totalsData?.stats.averageOffersViewed ?? data?.stats.averageOffersViewed ?? 0

  const captionText = intl.formatMessage({
    id: '0cJ2WB4I/g',
    defaultMessage: 'Offers Viewed',
    description: 'Offer views slide caption',
  })
  const headingTotals = intl.formatMessage({
    id: 'MAplvjdxsj',
    defaultMessage: 'In 2025, Luxury Escapes travellers explored',
    description: 'Offer views slide heading for totals-only mode',
  })
  const headingUser = intl.formatMessage({
    id: '+PM5VuJ3jH',
    defaultMessage: 'You found inspiration in',
    description: 'Offer views slide heading for user mode',
  })

  // Show totals version if in totals-only mode
  if (isTotalsOnlyMode) {
    return (
      <SlideBase background={background}>
        {animate && <FadeBackground>
          <Group direction="vertical" grow verticalAlign="end" horizontalAlign="start" gap={12}>
            <Group direction="vertical" gap={4}>
              <Caption colour="highlight-primary" variant="small" format="uppercase">{captionText}</Caption>
              <Heading variant="heading5">
                <b>
                  {headingTotals}
                </b>
              </Heading>
            </Group>
            <Heading variant="heading1alt">
              <FormattedMessage
                id="L5FC1SWskv"
                defaultMessage="{formatDecimal} offers"
                description="Offer views slide total offers count with FormatDecimal component"
                values={{
                  formatDecimal: <FormatDecimal value={totalOffersViewed} />,
                }}
              />
            </Heading>
          </Group>
        </FadeBackground>}
      </SlideBase>
    )
  }

  return (
    <SlideBase background={background}>
      {animate && <FadeBackground>
        <Group direction="vertical" grow verticalAlign="end" horizontalAlign="start" gap={12}>
          <Group direction="vertical" gap={4}>
            <Caption colour="highlight-primary" variant="small" format="uppercase">{captionText}</Caption>
            <Heading variant="heading5">
              <b>
                {headingUser}
              </b>
            </Heading>
          </Group>
          <Group direction="vertical" gap={4}>
            <Heading variant="heading1alt">
              <FormattedMessage
                id="XHwHjuKmji"
                defaultMessage="{formatDecimal} offers"
                description="Offer views slide user offers count with FormatDecimal component"
                values={{
                  formatDecimal: <FormatDecimal value={data?.stats.offersViewed ?? 0} />,
                }}
              />
            </Heading>
            <BodyText variant="medium">
              <FormattedMessage
                id="TukAgaqon4"
                defaultMessage="On average, our travellers viewed <b>{formatDecimal} offers</b> last year."
                description="Offer views slide average offers text with FormatDecimal component and bold formatting"
                values={{
                  formatDecimal: <FormatDecimal value={averageOffersViewed} />,
                  b: (chunks: React.ReactNode) => <b>{chunks}</b>,
                }}
              />
            </BodyText>
          </Group>
        </Group>
      </FadeBackground>}
    </SlideBase>
  )
}

export default React.memo(OfferViewsSlide)
